import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  TextField,
  Box,
  Card,
  CardContent,
  Grid,
  Skeleton,
  Alert,
  Divider,
} from "@material-ui/core";
import LoadingButton from "@material-ui/lab/LoadingButton";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import { IconButton } from "@material-ui/core";
import { useFormik, Form, FormikProvider } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import Page from "src/components/Page";
import { useSnackbar } from "src/components/snackbar";
import { clientesApi } from "src/services/clientesApi";
import { propuestasApi } from "src/services/propuestasApi";

const Schema = Yup.object().shape({
  correoFacturacion: Yup.string()
    .email("El correo de facturación no es válido")
    .required("El correo de facturación es obligatorio"),
});

export default function CompletarDatosFormales() {
  const { rut } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const [guardado, setGuardado] = useState(false);

  const {
    data: cliente,
    isLoading,
    refetch,
  } = clientesApi.useGetClienteQuery({ rut });

  const [editarCliente] = clientesApi.useEditarClienteMutation();

  const correoVacio =
    !guardado &&
    (!cliente?.correoFacturacion || cliente.correoFacturacion.trim() === "");

  const formik = useFormik({
    initialValues: { correoFacturacion: "" },
    validationSchema: Schema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        await editarCliente({
          rut,
          datos: { correoFacturacion: values.correoFacturacion.trim() },
        }).unwrap();
        enqueueSnackbar("Correo de facturación guardado", {
          variant: "success",
        });
        refetch();
        // El listado unificado vive en otra API (propuestasApi); su tag no se
        // invalida al editar el cliente. Lo invalidamos para que el chip y el
        // selector de datos pendientes se actualicen al volver.
        dispatch(propuestasApi.util.invalidateTags(["Propuestas"]));
        setGuardado(true);
      } catch (error) {
        enqueueSnackbar(error?.data?.message || "Error al guardar", {
          variant: "error",
        });
      } finally {
        setSubmitting(false);
      }
    },
  });

  const { errors, touched, isSubmitting, handleSubmit, getFieldProps } = formik;

  if (isLoading) {
    return (
      <Page title="Completar datos formales — Taxtic">
        <Container maxWidth="sm">
          <Box mt={4}>
            <Skeleton variant="text" width={320} height={40} />
            <Skeleton
              variant="rectangular"
              height={200}
              style={{ marginTop: 16, borderRadius: 8 }}
            />
          </Box>
        </Container>
      </Page>
    );
  }

  if (!cliente) {
    return (
      <Page title="Completar datos formales — Taxtic">
        <Container maxWidth="sm">
          <Box mt={4}>
            <Alert severity="error">No se encontró el cliente.</Alert>
          </Box>
        </Container>
      </Page>
    );
  }

  return (
    <Page title="Completar datos formales — Taxtic">
      <Container maxWidth="sm">
        <Box
          mt={2}
          mb={1}
          display="flex"
          alignItems="center"
          style={{ gap: 8 }}
        >
          <IconButton size="small" onClick={() => navigate(-1)}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Typography variant="h5" style={{ fontWeight: 700 }}>
            Completar datos formales
          </Typography>
        </Box>

        <Card style={{ borderRadius: 12, marginTop: 16 }} elevation={2}>
          <CardContent style={{ padding: "32px 28px" }}>
            {/* Identificación del cliente */}
            <Box mb={3}>
              <Typography variant="subtitle2" color="textSecondary">
                Cliente
              </Typography>
              <Typography variant="h6" style={{ fontWeight: 600 }}>
                {cliente.nombre}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                RUT: {rut}
              </Typography>
            </Box>

            <Divider style={{ marginBottom: 24 }} />

            {/* Estado: datos completos */}
            {!correoVacio ? (
              <Box>
                <Box
                  display="flex"
                  alignItems="center"
                  style={{ gap: 8, marginBottom: 12 }}
                >
                  <CheckCircleOutlineIcon
                    style={{ color: "#2e7d32", fontSize: 30 }}
                  />
                  <Typography
                    variant="h6"
                    style={{ color: "#2e7d32", fontWeight: 600 }}
                  >
                    Datos formales completos
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  style={{ marginBottom: 24 }}
                >
                  El cliente ya tiene registrado un correo de facturación.
                </Typography>
                <LoadingButton
                  variant="outlined"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate(-1)}
                >
                  Volver
                </LoadingButton>
              </Box>
            ) : (
              /* Estado: formulario */
              <Box>
                <Alert severity="info" style={{ marginBottom: 24 }}>
                  Para poder emitir y enviar facturas a este cliente es
                  necesario contar con su correo de facturación. Completa el
                  dato a continuación.
                </Alert>

                <FormikProvider value={formik}>
                  <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
                    <Grid container spacing={2}>
                      {/* ── Sección: datos de contacto formal ─────────── */}
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Correo Facturación"
                          variant="outlined"
                          type="email"
                          {...getFieldProps("correoFacturacion")}
                          error={Boolean(
                            touched.correoFacturacion &&
                            errors.correoFacturacion,
                          )}
                          helperText={
                            touched.correoFacturacion &&
                            errors.correoFacturacion
                          }
                        />
                      </Grid>
                    </Grid>

                    <Box mt={3} display="flex" style={{ gap: 12 }}>
                      <LoadingButton
                        type="submit"
                        variant="contained"
                        color="primary"
                        loading={isSubmitting}
                      >
                        Guardar
                      </LoadingButton>
                      <LoadingButton
                        variant="outlined"
                        onClick={() => navigate(-1)}
                        disabled={isSubmitting}
                      >
                        Cancelar
                      </LoadingButton>
                    </Box>
                  </Form>
                </FormikProvider>
              </Box>
            )}
          </CardContent>
        </Card>
      </Container>
    </Page>
  );
}
